// src/dashboard/DashboardShell.tsx
import { useVertical } from "@/shared/contexts/VerticalContext";
import { ChatLayout } from "@/shared/dashboard/layouts/ChatLayout";
import { ScenarioLayout } from "@/shared/dashboard/layouts/ScenarioLayout";
import { WIDGET_REGISTRY } from "@/shared/dashboard/widgets/registry";
import { useWorkspace } from "@/shared/dashboard/workspace/WorkspaceContext";
import { WorkspaceChat } from "@/shared/dashboard/workspace/WorkspaceChat";
import { WorkspaceHome } from "@/shared/dashboard/workspace/home/WorkspaceHome";

// Import every widget module for its side-effect registration.
import "@/shared/dashboard/widgets/SupervisorOpenerWidget";
import "@/shared/dashboard/widgets/ThreadsRailWidget";
import "@/shared/dashboard/widgets/ChatComposerWidget";
import "@/shared/dashboard/widgets/AssignedCourseHeroWidget";
import "@/shared/dashboard/widgets/AssignedCourseSyllabusWidget";
import "@/shared/dashboard/widgets/ProgressStatsWidget";
import "@/shared/dashboard/widgets/ContinueCoursesWidget";
import "@/shared/dashboard/widgets/DesktopAppLauncherWidget";
import "@/verticals/general/widgets/ConsumerDashboardEmptyWidget";
import "@/verticals/general/widgets/GeneralSkillsWidget";
import "@/verticals/general/widgets/GeneralCertificatesWidget";

/**
 * Picks a layout from the vertical's hero widget and delegates.
 * supervisorOpener → ChatLayout (chat-first); everything else stacks
 * in ScenarioLayout.
 */
export function DashboardShell() {
  const { verticalConfig } = useVertical();
  const workspace = useWorkspace();
  if (workspace)
    return workspace.catalogHome ? <WorkspaceHome /> : <WorkspaceChat />;
  const { hero, mainColumn, sideRail } = verticalConfig.dashboard;
  const heroWidgetId = hero.widget;

  let heroDef = WIDGET_REGISTRY[heroWidgetId];

  // Fallback: hero widget unknown OR doesn't support the "hero" slot.
  // Bad configs (e.g. authored via the builder) shouldn't crash the whole
  // dashboard — log a clear warning and substitute the default hero.
  const FALLBACK_HERO_ID = "supervisorOpener";
  if (!heroDef || !heroDef.supportedSlots.has("hero")) {
    console.warn(
      `[dashboard] hero widget "${heroWidgetId}" ${
        !heroDef
          ? "is not registered"
          : `does not support slot "hero" (supports: ${[...heroDef.supportedSlots].join(", ")})`
      } — falling back to "${FALLBACK_HERO_ID}". Fix the vertical config.`,
    );
    heroDef = WIDGET_REGISTRY[FALLBACK_HERO_ID as keyof typeof WIDGET_REGISTRY];
    if (!heroDef) return null;
  }

  const Layout =
    heroDef.id === "supervisorOpener" ? ChatLayout : ScenarioLayout;
  return (
    <Layout
      hero={{ widget: heroDef.id }}
      mainColumn={mainColumn}
      sideRail={sideRail}
    />
  );
}
